/**
 * Интеграции: на чём работает внешний сервис и что он делал.
 *
 * Состояние не хранится отдельным полем — оно следует из провайдера:
 * `off` → выключена, `stub` → работает на встроенной заглушке, `real` →
 * настоящий провайдер. Так состояние не может разойтись с настройкой (BR-47).
 */
import { arrayMove } from '@dnd-kit/sortable'
import type { ExchangePage, Integration, MapKey, MapKeyStatus } from './api'

export type Health = 'ok' | 'stub' | 'off'

/**
 * Системная роль «Супер-админ». Каналы оплаты переключает только она — сервер
 * проверяет роль, а не право `integrations:u` (R-113).
 */
export const SUPER_ADMIN_ROLE = 1

export const HEALTH_TEXT: Record<Health, string> = {
  ok: 'работает',
  stub: 'встроенная заглушка',
  off: 'выключена',
}

export function health(integration: Integration): Health {
  if (!integration.enabled || integration.provider === 'off') return 'off'
  return integration.provider === 'stub' ? 'stub' : 'ok'
}

/** Как называется провайдер на экране: коды короткие, смысл — нет. */
export const PROVIDER_TEXT: Record<string, string> = {
  off: 'выключено',
  stub: 'заглушка',
  real: 'настоящий провайдер',
}

export const providerText = (provider: string): string => PROVIDER_TEXT[provider] ?? provider

/**
 * Группы интеграций — в порядке, в каком их отдаёт сервер. Ключи известны
 * заранее, а незнакомый попадёт в «Прочее», а не потеряется.
 */
const GROUPS: Record<string, string> = {
  payment: 'Платежи',
  delivery: 'Логистика',
  geo: 'Карты и адреса',
  sms: 'Уведомления',
  telegram: 'Уведомления',
  push: 'Уведомления',
}

export const groupName = (key: string): string => GROUPS[key] ?? 'Прочее'

export function groupByCategory(integrations: Integration[]): [string, Integration[]][] {
  const groups = new Map<string, Integration[]>()
  integrations.forEach((integration) => {
    const name = groupName(integration.key)
    const list = groups.get(name) ?? []
    list.push(integration)
    groups.set(name, list)
  })
  return [...groups]
}

/** Буква вместо логотипа — как в прототипе. */
export const logoLetter = (integration: Integration): string =>
  (integration.title || integration.key).slice(0, 1).toUpperCase()

/** Сколько строк журнала на странице можно выбрать; по умолчанию — 10. */
export const LOG_PAGE_SIZES = [10, 25, 50] as const

/**
 * Подпись числа ошибок. Фильтра по статусу у журнала нет
 * (`GET /admin/integrations/{key}/log` знает только `page`, `page_size`),
 * поэтому ошибки считаются по последним обменам. Если страница вместила весь
 * журнал, это и есть все ошибки — тогда без оговорки.
 */
export const errorsLabel = (shown: number, total: number): string =>
  shown >= total ? 'Ошибок' : `Ошибок в последних ${shown}`

/**
 * Когда интеграция последний раз обменивалась, по первой странице общего
 * журнала (самые новые сверху). Нашлась — время; страница вместила весь
 * журнал, а обменов нет — 0, их правда не было; иначе `null`: обмен мог быть
 * раньше, и страница этого не знает. Журнал ещё не пришёл — тоже `null`.
 */
export function lastSync(page: ExchangePage | undefined, key: string): number | null {
  if (!page) return null
  const found = page.items.find((exchange) => exchange.key === key)
  if (found) return found.at
  return page.items.length >= page.total ? 0 : null
}

/** Что Яндекс сказал о ключе — словами для таблицы и тоном статуса. */
export const MAP_KEY_STATUS_TEXT: Record<MapKeyStatus, string> = {
  unknown: 'не проверялся',
  ok: 'работает',
  exhausted: 'лимит исчерпан',
  invalid: 'ключ неверный',
}

export const MAP_KEY_STATUS_TONE: Record<MapKeyStatus, string | undefined> = {
  unknown: undefined,
  ok: 'success',
  exhausted: 'warning',
  invalid: 'danger',
}

/** Интеграция, у которой есть пул ключей Яндекс Карт. */
export const MAPS_INTEGRATION = 'geo'

export const sortedMapKeys = (list: MapKey[]): MapKey[] => [...list].sort((a, b) => a.sort - b.sort)

/**
 * Перенос ключа на место другого. Прежние значения порядка раздаются по
 * новому списку — сохранять приходится только ключи между двумя местами.
 * Значения повторяются (ключи добавлены с одним `sort`) — раздаём 1, 2, 3…
 */
export function reorderMapKeys(list: MapKey[], activeId: number, overId: number): MapKey[] {
  const all = sortedMapKeys(list)
  const from = all.findIndex((k) => k.id === activeId)
  const to = all.findIndex((k) => k.id === overId)
  if (from < 0 || to < 0) return all
  const sorts = all.map((k) => k.sort)
  const distinct = new Set(sorts).size === sorts.length
  return arrayMove(all, from, to).map((k, i) => ({
    ...k,
    sort: distinct ? (sorts[i] as number) : i + 1,
  }))
}

/** Ключи, у которых после переноса поменялся порядок. */
export function changedMapKeys(before: MapKey[], after: MapKey[]): MapKey[] {
  return after.filter((k) => before.find((b) => b.id === k.id)?.sort !== k.sort)
}

/** Новый ключ встаёт последним: порядок остальных не трогаем. */
export const nextMapKeySort = (list: MapKey[]): number =>
  list.reduce((max, k) => Math.max(max, k.sort), 0) + 1

/** Ключ из кабинета Яндекса: 10–200 знаков без пробелов (так проверяет сервер). */
export function mapKeyError(value: string): string | null {
  const key = value.trim()
  if (!key) return 'Вставьте ключ'
  if (key.length < 10 || key.length > 200 || /\s/.test(key)) {
    return 'Это не похоже на ключ: 10–200 знаков без пробелов'
  }
  return null
}
